import Link from 'next/link';
import { Fragment } from 'react';

import { useCommonData } from '@app/hooks/useCommonData';
import { useLayout } from '@app/hooks/useLayout';
import { cn } from '@app/libs/utils';

import LocationIcon from './icons/LocationIcon';
import MailIcon from './icons/MailIcon';
import PhoneIcon from './icons/PhoneIcon';
import LogoImage from './LogoImage';

interface FooterProps {
  showFull?: boolean;
  className?: string;
}

export const Footer: React.FC<FooterProps> = ({ className = '', showFull }) => {
  const {
    navigation: { subfooter },
  }: any = useCommonData();
  const {
    website,
    website: { contact, social },
    navigation: { footer },
  }: any = useCommonData();
  const { showFooter } = useLayout();

  showFull = showFull === undefined ? showFooter : showFull;

  return (
    <footer className={cn('w-full', className)}>
      {showFull && (
        <div className="w-full bg-gray-100">
          <div className="w-auto 2xl:w-[90rem] mx-auto py-10 px-5 sm:px-6 text-gray-900 flex gap-40">
            <div className="flex flex-col items-start gap-4">
              <LogoImage variant="full" size="md" theme="light" className="h-8 w-auto" />
              <div className="text-sm text-gray-500 max-w-3xl">{website.description}</div>
              <div className="flex flex-row sm:flex-col gap-10 sm:gap-2 mt-2 text-gray-600">
                <div className="flex items-center gap-2 justify-start">
                  <LocationIcon size={18} className="text-gray-500" />
                  <span className="text-sm">{contact?.address}</span>
                </div>
                <div className="flex items-center gap-2 justify-start">
                  <PhoneIcon className="text-gray-500" />
                  <span className="text-sm">{contact?.phone}</span>
                </div>
                <div className="flex items-center gap-2 justify-start">
                  <MailIcon className="text-gray-500" />
                  <span className="text-sm">{contact?.email}</span>
                </div>
              </div>
              <ul className="flex gap-4 items-center flex-nowrap">
                {Object.entries(social).map(([key, value]) => (
                  <li key={key}>
                    <Link href={value as string} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`/images/${key.toLowerCase()}.png`}
                        alt={key}
                        width={24}
                        height={24}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-start gap-15 justify-between grow">
              {footer.map((item: any) => (
                <div key={item.label} className="flex flex-col items-start gap-4">
                  <h3 className="text-sm font-semibold text-gray-900">{item.label}</h3>
                  <ul className="flex flex-col gap-4 items-start flex-nowrap">
                    {item.menu.map((item: any) => (
                      <li key={item.label}>
                        <Link href={item.href}>{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="w-auto 2xl:w-[90rem] mx-auto py-4 px-5 sm:px-6 text-xs text-gray-900 flex justify-between flex-wrap">
        <div>
          © {new Date().getFullYear()} {website.name}. All rights reserved.
        </div>
        <ul className="flex gap-2 items-center flex-nowrap">
          {subfooter.map((item: any, index: number, array: any[]) => (
            <Fragment key={item.label}>
              <li>
                <Link
                  href={item.href}
                  className="border-b border-transparent hover:border-gray-900  transition-all cursor-pointer"
                >
                  <span dangerouslySetInnerHTML={{ __html: item.label }} />
                </Link>
              </li>
              {index !== array.length - 1 && <li className="text-gray-500">&#9670;</li>}
            </Fragment>
          ))}
        </ul>
      </div>
    </footer>
  );
};
